'use client'

import type { GradeSlice, RevealStages } from '@kanjido/database'

import { useKanji } from '@/kanjivg/hooks'
import type { KanjiStrokeData } from '@/kanjivg/types'
import { getSliceCells, getStageSlices } from '@/kanjivg/utils'

// Stroke-order strip — Learn spec §2.2.
//
// Cells render against a low-opacity ghost of the complete kanji. What each
// cell DRAWS is decided by the shared getStageSlices() / getSliceCells()
// helpers, which read `kanji.reveal_stages` / `kanji.grade_slice`:
//   - gradeSlice non-empty → delta cells per curated [start, end] range, no
//     complete cell appended (slice-class frames, canon spec §1.2)
//   - revealStages: [c1, c2, …] → cumulative checkpoint cells (book-faithful
//     staging), complete cell appended unless the last cut already covers it
//   - revealStages: false → step is excluded from the reveal sequence at the
//     <LearnFrame> level via useKanjiLearningSequence's hasStrokes flag;
//     this component never receives it as a visible row.
//   - revealStages: null → cumulative one cell per stroke (the fallback)
//
// The ghost is what makes staged-delta cells readable (without it, cell 2 of
// 朝 would read as four floating strokes; with it, the user sees them landing
// on the silhouette of 朝). Atlas's <ProgressiveStrokes> omits the ghost
// because its surrounding editorial prose frames the cells; Learn carries it.
const GHOST_OPACITY = 0.16

export function StrokeAffordance({
    character,
    strokeCount,
    revealStages,
    gradeSlice,
    strokeData,
    strokeHost,
    centered = false,
}: {
    character: string
    strokeCount: number
    revealStages: RevealStages | null
    gradeSlice: GradeSlice | null
    // Server-fetched stroke data, host-resolved display data
    // (loadDisplayStrokeData) — always matches the `host` fetch key below.
    strokeData?: KanjiStrokeData | null
    // DB-driven host override (kanji.stroke_host) — see host resolution below.
    strokeHost?: string | null
    // `centered` wraps + centers the cells (used in the focused draw beat where
    // the canvas below is centered); default is the left-aligned scroll strip
    // for the study reveal.
    centered?: boolean
}) {
    // Host resolution is DB-driven (kanji.stroke_host) — threaded from the
    // server loader. Preloaded strokeData is host-resolved display data
    // (loadDisplayStrokeData), so it always matches the fetch key.
    const host = strokeHost ?? character
    const { data, loading } = useKanji(host, strokeData ?? null)

    if (loading) {
        return (
            <div className="border-y border-rule-soft py-4">
                <div className="flex gap-3 overflow-x-auto">
                    {Array.from({ length: Math.min(strokeCount, 6) }).map(
                        (_, i) => (
                            <div
                                key={i}
                                className="size-14 shrink-0 animate-pulse rounded-sm bg-surface-sunken"
                            />
                        )
                    )}
                </div>
            </div>
        )
    }
    if (!data) return null

    const slices = gradeSlice?.length
        ? getSliceCells(data.strokePaths, gradeSlice)
        : getStageSlices(data.strokePaths, revealStages)
    if (slices.length === 0) return null

    // Ghost = union of strokes the strip will ever render across all cells.
    // For full-character cases (NULL fallback, checkpoint cuts of the host
    // character itself, cuts ending at the full character) this equals
    // data.strokePaths, so the ghost still shows the complete silhouette and
    // there's no visual regression. For host-substituted primitives (丷
    // rendered via 首, 𠂇 rendered via 有) curated with a non-empty
    // `gradeSlice`, the ghost is capped to the primitive's own delta-range
    // strokes — without this, the host's extra strokes show through at
    // GHOST_OPACITY and leak the substitution into the surface.
    const ghostPaths = Array.from(new Set(slices.flat()))

    return (
        <section
            aria-label={`Stroke order — ${strokeCount} strokes`}
            className="border-y border-rule-soft py-4"
        >
            <div
                className={
                    centered
                        ? 'mb-3 text-center text-meta text-muted-fg uppercase'
                        : 'mb-3 text-meta text-muted-fg uppercase'
                }
            >
                {strokeCount} {strokeCount === 1 ? 'stroke' : 'strokes'}
            </div>
            <ol
                className={
                    centered
                        ? 'flex flex-wrap justify-center gap-3 pb-1'
                        : 'flex scrollbar-thin gap-3 overflow-x-auto pb-1'
                }
            >
                {slices.map((paths, i) => (
                    <li key={i} className="shrink-0">
                        <svg
                            viewBox={data.viewBox}
                            width={56}
                            height={56}
                            className="text-body-fg"
                            style={{
                                fill: 'none',
                                stroke: 'currentColor',
                                strokeWidth: 4,
                                strokeLinecap: 'round',
                                strokeLinejoin: 'round',
                            }}
                            aria-hidden="true"
                        >
                            {/* Ghost layer — silhouette of all strokes the strip will reveal */}
                            {ghostPaths.map((d, j) => (
                                <path
                                    key={`g-${j}`}
                                    d={d}
                                    opacity={GHOST_OPACITY}
                                />
                            ))}
                            {/* Active layer — this cell's strokes at full opacity */}
                            {paths.map((d, j) => (
                                <path key={`a-${j}`} d={d} />
                            ))}
                        </svg>
                    </li>
                ))}
            </ol>
        </section>
    )
}
